import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import {
  Btn,
  ChipToggle,
  DataTable,
  LoadingBlock,
  Modal,
  PageHeader,
  inputStyle,
} from "../rule-builder/ui";
import { usePermissions } from "../login/PermissionContext";
import {
  STATUS_META as LAB_STATUS_META,
  allowedPatientTypeTabs,
  fetchAllLabTestPatients,
  formatDateTime,
} from "../lab-technician/labOrdersShared";
import {
  STATUS_META as RAD_STATUS_META,
  fetchAllRadiologyTestPatients,
} from "../radiology-technician/radiologyOrdersShared";
import { DELIVERED, setOrderDelivered } from "./api";

// A test can be handed over once it has reached at least Completed. Cancelled
// sits outside the run and never qualifies. The names are shared by the lab
// and radiology vocabularies.
const DELIVERABLE_STATUSES = ["Completed", "Reported", "Verified"];

const STATUS_META = {
  ...LAB_STATUS_META,
  ...RAD_STATUS_META,
  [DELIVERED]: { bg: "#DCFCE7", color: "#166534" },
};

const PATIENT_TYPES = [
  { id: "ALL", label: "All" },
  { id: "OPD", label: "OPD" },
  { id: "IPD", label: "IPD" },
  { id: "EMERGENCY", label: "Emergency" },
];

const SOURCES = ["All", "Lab", "Radiology"];
const PAGE_SIZE = 25;

const flatten = (patients, source) =>
  (patients || []).flatMap((patient) =>
    (patient.orders || []).map((order) => ({
      id: `${source}:${patient.id}:${order.orderNo}`,
      source,
      patient,
      order,
    })),
  );

function StatusPill({ status }) {
  const m = STATUS_META[status] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

export default function LabChecklist() {
  const [patientTypeTab, setPatientTypeTab] = useState("ALL");
  const { userInfo } = usePermissions();
  const allowedTabs = allowedPatientTypeTabs(userInfo?.facility_type);
  // "ALL" is not offered to an OPD-only facility, same as the lab board.
  const patientType = allowedTabs.includes(patientTypeTab)
    ? patientTypeTab
    : allowedTabs[0];
  const [source, setSource] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRow, setConfirmRow] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { patientType };
      const [lab, rad] = await Promise.all([
        fetchAllLabTestPatients(axios, getAuthHeaders(true), query),
        fetchAllRadiologyTestPatients(axios, getAuthHeaders(true), query),
      ]);
      const next = [...flatten(lab, "Lab"), ...flatten(rad, "Radiology")];
      next.sort((a, b) =>
        String(b.order.ordered || "").localeCompare(String(a.order.ordered || "")),
      );
      setRows(next);
    } catch (err) {
      console.error("CheckList load failed:", err);
      toast.error(err?.message || "Could not load tests");
    } finally {
      setLoading(false);
    }
  }, [patientType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [patientType, source, statusFilter, search]);

  const statusOptions = useMemo(
    () => [...new Set(rows.map((r) => r.order.status).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (source !== "All" && r.source !== source) return false;
      if (statusFilter !== "All statuses" && r.order.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      return [r.patient.name, r.patient.uhid, r.order.orderNo, r.order.service]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, source, statusFilter, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setRowStatus = (id, status) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, order: { ...r.order, status } } : r)),
    );

  const markDelivered = async () => {
    const row = confirmRow;
    setConfirmRow(null);
    if (!row) return;
    const previous = row.order.status;
    setSavingId(row.id);
    setRowStatus(row.id, DELIVERED);
    try {
      await setOrderDelivered(row.patient, row.order);
      toast.success(`${row.order.orderNo} → ${DELIVERED}`);
    } catch (err) {
      console.error(`Failed to mark ${row.order.orderNo} delivered:`, err);
      setRowStatus(row.id, previous);
      toast.error(err?.message || "Could not mark as delivered");
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    {
      key: "orderNo",
      label: "Order No",
      render: (r) => <span style={{ fontWeight: 600 }}>{r.order.orderNo}</span>,
    },
    { key: "source", label: "Type", render: (r) => r.source },
    {
      key: "patient",
      label: "Patient",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: C.text }}>{r.patient.name || "-"}</div>
          <div style={{ fontSize: 11, color: C.textSub }}>{r.patient.uhid || "-"}</div>
        </div>
      ),
    },
    { key: "patientType", label: "Patient Type", render: (r) => r.patient.patientType || "-" },
    { key: "test", label: "Test", render: (r) => r.order.service || "-" },
    { key: "ordered", label: "Ordered", render: (r) => formatDateTime(r.order.ordered) },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.order.status} /> },
    {
      key: "action",
      label: "Action",
      align: "right",
      render: (r) => {
        if (r.order.status === DELIVERED) return null;
        const allowed = DELIVERABLE_STATUSES.includes(r.order.status);
        const reason =
          r.order.status === "Cancelled"
            ? "Cancelled tests can't be delivered"
            : "Available once Completed";
        return (
          <span title={allowed ? "" : reason}>
            <Btn
              size="sm"
              disabled={!allowed || savingId === r.id}
              onClick={() => setConfirmRow(r)}
            >
              Mark Delivered
            </Btn>
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="CheckList"
        subtitle="All lab and radiology tests. Completed, Reported or Verified tests can be marked Delivered."
        actions={
          <Btn variant="outline" onClick={load} disabled={loading}>
            Refresh
          </Btn>
        }
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {PATIENT_TYPES.filter((t) => allowedTabs.includes(t.id)).map((t) => (
          <ChipToggle
            key={t.id}
            active={patientType === t.id}
            onClick={() => setPatientTypeTab(t.id)}
          >
            {t.label}
          </ChipToggle>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          style={{ ...inputStyle, maxWidth: 320 }}
          placeholder="Search patient, UHID, order no, test"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ ...inputStyle, maxWidth: 180 }}
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s === "All" ? "Lab & Radiology" : s}
            </option>
          ))}
        </select>
        <select
          style={{ ...inputStyle, maxWidth: 220 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All statuses">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingBlock message="Loading tests…" />
      ) : (
        <>
          <DataTable columns={columns} rows={pageRows} emptyText="No tests found" />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 12,
              color: C.textSub,
            }}
          >
            <span>{filtered.length} tests</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Btn
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </Btn>
              <span>
                Page {page} of {pageCount}
              </span>
              <Btn
                size="sm"
                variant="outline"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Btn>
            </div>
          </div>
        </>
      )}

      <Modal
        open={!!confirmRow}
        onClose={() => setConfirmRow(null)}
        title="Mark as Delivered?"
        description={
          confirmRow
            ? `${confirmRow.order.orderNo} · ${confirmRow.order.service || ""} · ${
                confirmRow.patient.name || ""
              }`
            : ""
        }
        footer={
          <>
            <Btn variant="outline" onClick={() => setConfirmRow(null)}>
              Cancel
            </Btn>
            <Btn onClick={markDelivered}>Mark Delivered</Btn>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: C.text }}>
          This confirms the report has been handed over to the patient.
        </p>
      </Modal>
    </div>
  );
}
